//@ts-check

class PaymentApprovalService {
	constructor($http, Utility, $timeout, $rootScope, ContentService, LocalStorageService, InternalLinksService) {
		this.$http = $http;
		this.Utility = Utility;
		this.$timeout = $timeout;
		this.$rootScope = $rootScope;
		this.ContentService = ContentService;
		this.LocalStorageService = LocalStorageService;
		this.InternalLinksService = InternalLinksService;

		this.init();
	}

	init() {
		this.approvalsData = undefined;
		this.paymentTypes = undefined;
		this.serviceProviders = undefined;
		this.numberOfResults = 0;
		this.InternalLinksService.getLinks('/api/payments/links');
	}

	/**
	 * Method for fetching payments to be approved
	 * @param {string} schemeRef The building scheme reference
	 * @return {Promise<Object>} The payments promise
	 */
	async getPaymentApprovals(schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApi');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApi');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			const currentCount = results.data && results.data.length ? results.data.length : 0;

			let approvalsData = results.data ? results.data : results;

			approvalsData = this._mapApprovals(approvalsData);

			const oldApprovalsData = this.approvalsData;

			if (oldApprovalsData && oldApprovalsData.hasOwnProperty('approvals') && oldApprovalsData.approvals) {
				oldApprovalsData.approvals = approvalsData;
			} else {
				this.approvalsData = $.extend({ approvals: approvalsData }, oldApprovalsData);
			}

			this.$rootScope.$broadcast('approvals-count-changed', currentCount);

			this.$timeout(() => promiseHandler.resolve(approvalsData));
		} catch (err) {
			const payments = {
				approvals: [],
				approved: []
			};
			this.approvalsData = payments;
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for fetching approved payments
	 * @param {string} schemeRef The building scheme reference
	 * @return {Promise<Object>} The payments promise
	 */
	async getApprovedPayments(schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiApproved');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiApproved');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			const approvedData = results.data ? results.data : results;
			const oldApprovedData = this.approvalsData;

			if (oldApprovedData && oldApprovedData.hasOwnProperty('approved') && oldApprovedData.approved)
				oldApprovedData.approved = approvedData;
			else {
				this.approvalsData = $.extend({ approved: approvedData }, oldApprovedData);
			}
			this.$timeout(() => promiseHandler.resolve(approvedData));
		} catch (err) {
			const payments = {
				approvals: [],
				approved: []
			};
			this.approvalsData = payments;
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for approving a payment
	 * @param {string} paymentId The payment ID
	 * @param {string} schemeRef The building scheme reference
	 * @return {Promise<Object>} The results promise
	 */
	async approvePayment(paymentId, schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!paymentId) {
				throw new Error('No payment id provided...');
			}
			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiApprove');
			}

			url = url.replace('{paymentId}', paymentId);
			const results = await this.$http.post(url);
			const { data } = results;

			// await this.getApprovedPayments(schemeRef);
			await this.getPaymentApprovals(schemeRef);

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async submitPaymentForApporval(paymentDataPayload, arrayOfAttachmentBase64Blobs) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('SubmitPaymentForApproval');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('SubmitPaymentForApproval');
			}

			const results = await this.postAsFormData(paymentDataPayload, arrayOfAttachmentBase64Blobs, url);
			const { data } = results;

			this.$timeout(() => promiseHandler.resolve(results));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}
	async canSubmitPaymentForApproval(request) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!request) {
				throw new Error('No payment data provided...');
			}
			let url = await this.InternalLinksService.getLink('CanSubmitPaymentForApproval');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('CanSubmitPaymentForApproval');
			}

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, request, { headers });
			let cansubmit = results.data ? results.data : results;

			cansubmit = cansubmit.Data;

			this.$timeout(() => promiseHandler.resolve(cansubmit));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async getCurrentVatRateForScheme(schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}
			let url = await this.InternalLinksService.getLink('GetCurrentVatRateForScheme');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('GetCurrentVatRateForScheme');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			let currentVatRate = results.data;

			this.$timeout(() => promiseHandler.resolve(currentVatRate));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async isPaymentBandEnabledBySchemeRef(schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}
			let url = await this.InternalLinksService.getLink('IsPaymentBandEnabledBySchemeRef');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('IsPaymentBandEnabledBySchemeRef');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			let isPaymentBandEnabled = results.data ? results.data : results;

			isPaymentBandEnabled = isPaymentBandEnabled.Data;

			this.$timeout(() => promiseHandler.resolve(isPaymentBandEnabled));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for approving a bulk of payments
	 * @param {string} schemeRef The building scheme reference
	 * @param {Array<string>} IDs The selected payment IDs for approval
	 * @return {Promise<Object>} The results promise
	 */
	async bulkApprovePayments(schemeRef, IDs) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			if (!IDs) {
				throw new Error('No selected Payment IDs for approval provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiBulkApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiBulkApprove');
			}

			const payload = {
				PaymentIds: IDs
			};

			const results = await this.$http.post(url, payload);
			const { data } = results;

			// await this.getApprovedPayments(schemeRef);
			await this.getPaymentApprovals(schemeRef);

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for querying a payment
	 * @param {string} paymentId The payment Id of the payment to be queried
	 * @param {string} message The query message
	 * @return {Promise<Object>} The results promise
	 */
	async queryPayment(paymentId, message, schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!message) {
				throw new Error('No Query message provided...');
			}
			if (!paymentId) {
				throw new Error('No Payment ID provided...');
			}
			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiQuery');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiQuery');
			}

			const payload = {
				Message: message,
				PaymentId: paymentId
			};

			const results = await this.$http.post(url, payload);
			const { data } = results;

			await this.getPaymentApprovals(schemeRef);

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for fetching the payments content
	 * @return {Promise<string>} The html promise
	 */
	async loadContent() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			const content = await this.ContentService.load('PaymentsContent', '/api/payments/links');
			this.$timeout(() => promiseHandler.resolve(content));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Helper Method for mapping payment IDs 
	 * @param {string} schemeRef
	 * @return {Array<string>} The payment IDs
	 */
	_getPaymentIds(schemeRef) {
		let ids = [];
		ids = _.map(this.approvalsData.approvals, 'PaymentId');

		return ids;
	}

	/**
	 * Method for un-approving a specific payment
	 * @param {string} paymentId The payment ID
	 * @return {Promise<Object>} Asynchronous process
	 */
	async unApprovePayment(paymentId, schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!paymentId) {
				throw new Error('No payment id provided...');
			}
			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentUnApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentUnApprove');
			}

			url = url.replace('{paymentId}', paymentId);
			const results = await this.$http.post(url);
			const { data } = results;

			// await this.getApprovedPayments(schemeRef);
			await this.getPaymentApprovals(schemeRef);

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async searchPayments(webPaymentSearch) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			const schemeRef = webPaymentSearch.SchemeReference;

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentSearch');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentSearch');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, webPaymentSearch, { headers });

			const approvedData = results.data.Data ? results.data.Data : results;
			const oldApprovedData = this.approvalsData;

			this.numberOfResults = results.data.NumberOfResults;

			if (oldApprovedData && oldApprovedData.hasOwnProperty('approved') && oldApprovedData.approved)
				oldApprovedData.approved = approvedData;
			else {
				this.approvalsData = $.extend({ approved: approvedData }, oldApprovedData);
			}
			this.$timeout(() => promiseHandler.resolve(approvedData));
		} catch (err) {
			const payments = {
				approved: []
			};
			this.approvalsData = payments;
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Helper Method for mapping payment IDs 
	 * @param {Array<Object>} approvals The payment approvals
	 * @return {Array<string>} The payment IDs
	 */
	_mapApprovals(approvals) {
		approvals.forEach((payment, index) => {
			payment.Checked = payment.hasOwnProperty('Checked') ? payment.Checked : false;

			payment.isQuerying = false;
			payment.isApproving = false;
			payment.isUnApproving = false;

			payment.Attachments.forEach((attachment, innerIndex) => {
				attachment.Path = `${attachment.Path}${attachment.DocumentExtension}`;
			});
		});

		return approvals;
	}

	async getPaymentTypes() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('PaymentTypes');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentTypes');
			}
			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.get(url, { headers });

			let paymentTypes = results.data ? results.data : results;

			this.paymentTypes = paymentTypes.Data;

			this.$timeout(() => promiseHandler.resolve(this.paymentTypes));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async searchServiceProvider(searchTerm) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!searchTerm) {
				throw new Error('No search service term provided...');
			}

			let url = await this.InternalLinksService.getLink('ServiceProviderSearch');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('ServiceProviderSearch');
			}

			const headers = this.Utility.headersWithContentType;
			const results = await this.$http.post(url, searchTerm, { headers });

			let serviceProviderResult = results.data ? results.data : results;
			this.serviceProviders = serviceProviderResult.Data;

			this.$timeout(() => promiseHandler.resolve(this.serviceProviders));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async checkInvoiceNumberHasDuplicate(serviceProviderId, invoiceNumber) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			let url = await this.InternalLinksService.getLink('CheckInvoiceNumberHasDuplicate');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('CheckInvoiceNumberHasDuplicate');
			}

			const payload = {
				serviceProviderId,
				invoiceNumber
			};

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, payload, { headers });

			let hasDuplicateResult = results.data ? results.data : results;

			let hasDuplicate = hasDuplicateResult.Data;

			this.$timeout(() => promiseHandler.resolve(hasDuplicate));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async postAsFormData(objToBeHttpPosted, arrayOfBase64Blobs, link) {

		var formdata = new FormData();

		formdata.append('JSONObject', JSON.stringify(objToBeHttpPosted));

		for (let index = 0; index < arrayOfBase64Blobs.length; index++) {
			const element = arrayOfBase64Blobs[index];
			formdata.append(`BlobBase64${index}`, new Blob([element], {
				type: "application/pdf"
			})
			);
		}

		let headers = this.Utility.headers;
		headers = {
			...headers,
			'content-type': undefined
		};

		return this.$http({
			url: link,
			headers: headers,
			data: formdata,
			method: "POST",
			transformRequest: angular.identity
		});
	}

	async SearchGLAccounts(searchTerm, schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});
			if (!searchTerm) {
				throw new Error('no initial request data');
			}
			let url = await this.InternalLinksService.getLink('SearchForAccountNames');
			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('SearchForAccountNames');
			}

			const payload = {
				searchTerm,
				schemeRef
			};

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, payload, { headers });
			const accountsData = results.data ? results.data : results;
			this.$timeout(() => promiseHandler.resolve(accountsData));
		} catch (error) {
			const msg = `Error while searching available accountnames.`;
			console.error(msg, error);
			this.$timeout(() => promiseHandler.reject(error));
		}

		return promise;
	}
}

export default /* @ngInject */ (
	$http,
	Utility,
	$timeout,
	$rootScope,
	ContentService,
	LocalStorageService,
	InternalLinksService
) =>
	new PaymentApprovalService(
		$http,
		Utility,
		$timeout,
		$rootScope,
		ContentService,
		LocalStorageService,
		InternalLinksService
	);
